import { Box, CircularProgress, Grid } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  deleteSavedQuery,
  editQuery,
  getConfirmations,
  getConfirmationsFilters,
  getSavedQuery,
  saveQuery,
} from '../../../packages/common/api';
import WarningModal from '../../../packages/common/shared-ui/src/components/WarningModal';
import { ButtonsTypes } from '../../../packages/common/shared-ui/src/enums/commonEnums';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import {
  formatFilters,
  formatQueryFilters,
  formatQueryToSave,
  formattedParamsData,
  paramsToQuery,
  queryToParams,
} from '../../../packages/common/utils/gridFiltersHelperNew';
import CommonPage from '../CommonPage/CommonPage';
import GridFiltersModalNew from '../GridFiltersModalNew/GridFiltersModalNew';
import GridFiltersPanelNew from '../GridFiltersPanelNew/GridFiltersPanelNew';
import GridSaveQueryModal from '../GridSaveQueryModal/GridSaveQueryModal';

import ConfirmationsTable from './components/ConfirmationsTable';

const ConfirmationsPage = () => {
  const getLocalString = useTranslation();
  const navigate = useNavigate();
  const pageUseParams = useParams();
  const [queryData, setQueryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [openParamsModal, setOpenParamsModal] = useState(false);
  const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
  const [currentParams, setCurrentParams] = useState(null);
  const [initialParams, setInitialParams] = useState(null);
  const [gridRows, setGridRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [queryModalState, setQueryModalState] = useState(null);
  const [query, setQuery] = useQueryParams({
    filters: StringParam,
    sorts: StringParam,
    fields: StringParam,
    offset: NumberParam,
  });
  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  //Получение фильтров
  useEffect(() => {
    let ignore = false;
    let params = {};
    pageUseParams.queryId && (params.query = Number(pageUseParams.queryId));
    pageUseParams.projectId && (params.project = pageUseParams.projectId);
    async function getFiltersData() {
      const response = await getConfirmationsFilters(params);
      if (!ignore) {
        return response;
      }
    }

    getFiltersData()
      .then(res => {
        const { data: paramsData } = res;
        const params = formatFilters(paramsData);
        !initialParams && setInitialParams(params);
      })
      .catch(error => handleError(error.response));
  }, [handleError, pageUseParams.projectId, pageUseParams.queryId]);

  //Получение query по id
  useEffect(() => {
    setQueryData(null);
    let ignore = false;
    async function getSavedQueries() {
      const { data: response } = await getSavedQuery({
        id: pageUseParams.queryId,
      });
      if (!ignore) {
        return response;
      }
    }
    if (pageUseParams.queryId) {
      getSavedQueries()
        .then(res => {
          res && setQueryData(res);
        })
        .catch(error => handleError(error.response));
    }
  }, [handleError, pageUseParams.queryId]);

  //Получение списка согласований
  useEffect(() => {
    let ignore = false;
    let params = {};
    setParamsLoading(true);
    let queryIsSet = false;
    queryIsSet =
      Object.values(query).filter(item => item !== undefined).length > 0;
    pageUseParams.projectId &&
      (params.project = Number(pageUseParams.projectId));
    queryIsSet && (params = { ...params, ...query });
    async function getConfirmationsData() {
      const response = await getConfirmations(params);
      if (!ignore) {
        return response;
      }
    }
    if (queryIsSet) {
      getConfirmationsData()
        .then(res => {
          const { data: confirmationsData } = res;
          confirmationsData.fields && setGridColumns(confirmationsData.fields);
          confirmationsData.rows && setGridRows(confirmationsData.rows);
          confirmationsData.paging && setPagination(confirmationsData.paging);
          setLoading(false);
          setParamsLoading(false);
        })
        .catch(error => handleError(error.response));
    }
    return () => {
      ignore = true;
    };
  }, [handleError, pageUseParams.projectId, query]);

  useEffect(() => {
    if (!pageUseParams.queryId && initialParams) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams));
        setCurrentParams(initialParams);
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [initialParams, pageUseParams.queryId, setQuery]);
  useEffect(() => {
    if (pageUseParams.queryId && initialParams && queryData) {
      if (Object.values(query).every(x => x === undefined)) {
        setQuery(paramsToQuery(initialParams, queryData.query));
        setCurrentParams(formatQueryFilters(initialParams, queryData.query));
      } else {
        setCurrentParams(queryToParams(query, initialParams));
      }
    }
  }, [
    initialParams,
    pageUseParams.queryId,
    setQuery,
    queryData,
    setCurrentParams,
  ]);

  const handleParamsModalClose = useCallback(() => {
    setOpenParamsModal(false);
  }, []);
  const handleSaveQueryModalClose = useCallback(() => {
    setOpenSaveQueryModal(false);
  }, []);

  const handleSelectFilters = useCallback(
    data => {
      if (!data.hasOwnProperty('operation')) {
        return;
      }
      const items = currentParams.filters.selectedItems.map(item => {
        if (item.fieldId === data.fieldId) {
          return data;
        } else {
          return item;
        }
      });
      const updatedParams = {
        ...currentParams,
        filters: { ...currentParams.filters, selectedItems: items },
      };
      setCurrentParams(updatedParams);
      setQuery(paramsToQuery(updatedParams));
    },
    [currentParams, setQuery],
  );

  const handleParamsModalSave = useCallback(
    data => {
      setCurrentParams(data);
      setQuery(paramsToQuery(data));
      setOpenParamsModal(false);
    },
    [setQuery],
  );

  const handleSaveQueryModalSave = useCallback(
    async modalData => {
      const value = formatQueryToSave(modalData, currentParams, 'confirmation');
      try {
        setSaveLoader(true);
        let response;
        if (queryModalState === 'edit') {
          response = await editQuery(pageUseParams.queryId, value);
        } else {
          response = await saveQuery(value);
        }
        if (response) {
          setSaveLoader(false);
          const { data: res } = response;
          if (pageUseParams.projectId) {
            window.open(
              `/projects/${pageUseParams.projectId}/confirmations/query/${res.id}`,
              '_self',
            );
          } else {
            window.open(`/confirmations/query/${res.id}`, '_self');
          }
        }
      } catch (error) {
        handleError(error.response);
      }
      setOpenSaveQueryModal(false);
    },
    [
      currentParams,
      queryModalState,
      pageUseParams.queryId,
      pageUseParams.projectId,
      handleError,
    ],
  );
  const handleChangeSorting = useCallback(
    newSortModel => {
      if (!newSortModel || newSortModel.length === 0) {
        setQuery(prev => ({ ...prev, sorts: undefined }));
        return;
      }

      const sortString = newSortModel
        .map(item => `${item.field};${item.sort}`)
        .join('$$');

      setQuery(prev => ({ ...prev, sorts: sortString }));
    },
    [setQuery],
  );
  const handleButtonEvent = useCallback(type => {
    switch (type) {
      case ButtonsTypes.PARAMS:
        setOpenParamsModal(true);
        break;
      case ButtonsTypes.QUERY:
        setQueryModalState('create');
        setOpenSaveQueryModal(true);
        break;
      case ButtonsTypes.EDIT_QUERY:
        setQueryModalState('edit');
        setOpenSaveQueryModal(true);
        break;
      case ButtonsTypes.DELETE_QUERY:
        setOpenWarningModal(true);
        break;
    }
  }, []);
  const handleChangePagination = useCallback(
    data => {
      setQuery(prev => ({ ...prev, offset: data * 100 }));
    },
    [setQuery],
  );
  const handleWarningModalClose = useCallback(() => {
    setOpenWarningModal(false);
  }, []);
  const handleToggleDelete = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await deleteSavedQuery({ id: pageUseParams.queryId });
          navigate('/confirmations');
          setOpenWarningModal(false);
          break;
        case 'delete':
          setOpenWarningModal(false);
          break;
      }
    },
    [pageUseParams.queryId, navigate],
  );
  return (
    <>
      <WarningModal
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleToggleDelete}
        closeWarningModal={handleWarningModalClose}
        title={`${getLocalString('messages', 'deleteQuery')}?`}
      />
      {currentParams && (
        <GridFiltersModalNew
          handleSave={data => handleParamsModalSave(data)}
          handleClose={handleParamsModalClose}
          open={openParamsModal}
          data={currentParams}
        />
      )}
      <GridSaveQueryModal
        state={queryModalState}
        saveLoader={saveLoader}
        permission={'canSaveConfirmationGlobalQueries'}
        queryData={queryData}
        open={openSaveQueryModal}
        handleSave={data => handleSaveQueryModalSave(data)}
        handleClose={handleSaveQueryModalClose}
      />
      <CommonPage
        loading={loading}
        buttonEvent={handleButtonEvent}
        queryName={queryData?.name}
        queryUserId={queryData?.userId}
        component={'Confirmations'}
      >
        {currentParams && currentParams.filters?.selectedItems?.length > 0 && (
          <Box sx={{ marginTop: '10px' }}>
            <GridFiltersPanelNew
              data={currentParams.filters.selectedItems}
              handleCloseFilter={handleSelectFilters}
            ></GridFiltersPanelNew>
          </Box>
        )}
        {gridColumns && gridRows && (
          <Grid
            container
            wrap='nowrap'
            sx={{
              overflow: 'hidden',
              flexDirection: 'column',
              position: 'relative',
              marginTop: '20px',
            }}
          >
            {paramsLoading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'absolute',
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            <ConfirmationsTable
              columnsData={gridColumns}
              rowsData={gridRows}
              changePagination={handleChangePagination}
              changeSorting={handleChangeSorting}
              paginationData={pagination}
            />
          </Grid>
        )}
      </CommonPage>
    </>
  );
};
export default ConfirmationsPage;
